/** Core English dictionary. Keys must match `sv.ts` exactly (parity test). */
import type { CoreDictionary } from "./sv";

export const en: Record<keyof CoreDictionary, string> = {
  "privacy.link": "Privacy and personal data",
  "auth.registrationGuidance":
    "Did your teacher give you a registration code? Enter it when creating your account.",
  "courseStart.title": "Modern physics HT2026 – start here",
  "courseStart.book":
    "Course book: Young & Freedman, University Physics, 15th ed., chapters 37–44. Modules 1–9 are course divisions, not book chapter numbers. The practice bank does not yet cover the full book range.",
  "courseStart.accounts":
    "Canvas and this website use separate sign-ins. Results are not automatically sent back to Canvas.",
  "courseStart.registration":
    "Sign in with Google and enter a name your teacher recognises when creating your account here. Use the same Google account when you return.",
  "courseStart.continue": "Continue to {lecture}",
  "courseStart.selection":
    "This link is for {lecture}. Check the lecture selection, then press Start. Selections for later lectures will be published over time.",
  "courseStart.open": "Open the lecture practice",
  "courseStart.reporting":
    "Reporting by lecture is not available yet. Practice activity does not show attendance or that you can solve a new problem without help.",
  "courseStart.delete": "You can delete your account and its saved data in Settings.",
  "courseStart.studyTitle": "How to use the exercises",
  "courseStart.study":
    "Try on your own first. Then use the hints and compare your reasoning with the solution. Return another day.",
  "courseStart.notFound": "Course or lecture not found. Check the Canvas link.",

  "app.title": "Modern Physics",
  "app.subtitle": "Practice for the exam",
  "app.skipToContent": "Skip to content",

  "nav.home": "Home",
  "nav.settings": "Settings",
  "nav.teacher": "Teacher",
  "nav.signOut": "Sign out",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  "nav.account": "Account",

  "common.loading": "Loading …",
  "common.save": "Save",
  "common.saving": "Saving …",
  "common.saved": "Saved",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "practice.setup.taxonomyError": "Could not load topics and course moments.",
  "common.retry": "Try again",
  "examGuard.checkFailed.title": "Could not check whether an exam is running",
  "examGuard.checkFailed.body":
    "The server did not answer in time. This page stays locked until we know no practice exam is running.",
  "common.back": "Back",
  "common.next": "Next",
  "common.delete": "Delete",
  "common.error": "Something went wrong",
  "common.nothingHere": "Nothing to show yet",
  "common.language": "Language",
  "common.theme": "Theme",
  "common.theme.light": "Light",
  "common.theme.dark": "Dark",
  "common.scrollableContent": "Scrollable content",
  "common.confidence.question": "How sure are you?",
  "common.confidence.1": "Guessing",
  "common.confidence.2": "Unsure",
  "common.confidence.3": "Fairly sure",
  "common.confidence.4": "Certain",

  "auth.email": "Email",
  "auth.displayName": "Name",
  "auth.signIn": "Sign in",
  "auth.signUp": "Create account",
  "auth.registrationCode": "Registration code",
  "auth.teacherCode": "Teacher code",
  "auth.privacyTitle": "What the teacher can see",
  "auth.privacyBody":
    "Your teacher sees your name, your email address and your course statistics: which problems you have practised, your scores and your results on practice exams. The teacher does not see what you type in the notes fields. You sign in with Google and have no password with us. From Google we only store your email address and an account ID. You need a Google account to use the portal. A free account is enough, and you can create it with any email address. You can delete your account at any time under Settings.",
  "auth.signedInAs": "Signed in as {name}",
  "auth.rateLimitedRetry.seconds": "Too many attempts. Try again in {seconds} s.",
  "auth.rateLimitedRetry.minutes.one": "Too many attempts. Try again in {minutes} minute.",
  "auth.rateLimitedRetry.minutes.other": "Too many attempts. Try again in {minutes} minutes.",
  "auth.google.signIn": "Sign in with Google",
  "auth.google.disabled": "Sign-in is temporarily turned off. Please try again later.",
  "auth.google.cancelled": "Google sign-in was cancelled.",
  "auth.google.expired":
    "The sign-in took too long or was started in another tab. Please try again.",
  "auth.google.unverified":
    "Your email address is not verified with Google. Verify it there and try again.",
  "auth.google.failed": "Google sign-in is not working right now. Please try again in a moment.",
  "auth.google.conflict":
    "An account with this email address already exists for a different Google account. Please contact your teacher.",
  "auth.google.rateLimited":
    "Too many failed sign-in attempts from this network. Please try again shortly.",
  "auth.google.completeTitle": "Create your account",
  "auth.google.completeAs": "You are creating an account as {email}.",
  "auth.google.iAmTeacher": "I am a teacher",
  "auth.google.pendingExpired": "That took too long. Please start again with Google.",
  "auth.google.notYou": "Not you? Start again",
  "auth.google.firstTime":
    "First time here? Sign in with Google and we create your account right after.",
  "auth.google.sharedComputer":
    "Using a shared computer? Sign out of Google too when you are done.",
  "auth.code.rateLimited": "Too many wrong codes. Please wait and try again.",
  "auth.privacyGoogle":
    "You sign in with Google. We only receive your email address and an account ID from Google — no password and nothing else from your Google account. You need a Google account. A free account is enough, and you can create it with any email address.",

  "landing.heading": "Practice portal for Modern Physics",
  "landing.body":
    "Problems by module, a mock exam in the real format, flashcards with spaced repetition, and a clear picture of what to study next.",
  "landing.ctaRegister": "Create account",
  "landing.ctaLogin": "Sign in",
  "landing.codes": "LGFY41 / L9FY41 / L9FY45",
  "landing.course": "Modern Physics, LGFY41 / L9FY41 / L9FY45 — University of Gothenburg",

  "home.greeting": "Hi {name}!",
  "home.subtitle": "What would you like to do today?",
  "home.quickLinks": "Quick start",
  "home.count.practice": "{count} questions to practise",
  "home.count.exam": "{count} past papers to sit",
  "home.count.flashcards.due.one": "{count} card due",
  "home.count.flashcards.due.other": "{count} cards due",
  "home.count.flashcards.new.one": "{count} new",
  "home.count.flashcards.new.other": "{count} new",
  "home.count.library": "{topics} topics · {formulas} formulas",
  "home.teacherCard": "Teacher view",
  "home.teacherCardBody": "Class statistics, reports and roles.",
  "home.activeExam.title": "Exam in progress",
  "home.activeExam.body": "Continue where you left off, or submit what you have.",
  "home.activeExam.link": "Continue the exam",

  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.preferences": "Study preferences",
  "settings.newCardsPerDay": "New flashcards per day",
  "settings.bonusPoints": "Bonus points from hand-ins",
  "settings.bonusHelp":
    "Bonus points only count towards a pass (G) and only when the raw score is at most 2 points below the G threshold.",
  "settings.dangerZone": "Delete account",
  "settings.deleteWarning":
    "Your account and all of your history are deleted permanently. This cannot be undone.",
  "settings.deleteAccount": "Delete my account",
  "settings.deleted": "Your account has been deleted.",
  "settings.teacher.title": "I am a teacher",
  "settings.teacher.submit": "Become a teacher",
  "settings.google.reauth":
    "For security, sign in with Google again before you delete your account.",
  "settings.google.reauthButton": "Sign in with Google again",
  "settings.google.reauthMismatch":
    "That was a different Google account from yours. Try again and choose the right account.",

  "nav.help": "Help",

  "home.examMode.badge": "Exam mode",
  "home.examMode.daysLeft.exam.one": "{count} day left to the exam ({date})",
  "home.examMode.daysLeft.exam.other": "{count} days left to the exam ({date})",
  "home.examMode.daysLeft.retake.one": "{count} day left to the re-sit ({date})",
  "home.examMode.daysLeft.retake.other": "{count} days left to the re-sit ({date})",
  "home.examMode.today.exam": "The exam is today ({date}). Good luck!",
  "home.examMode.today.retake": "The re-sit is today ({date}). Good luck!",
  "home.examMode.reviewingNoDate": "You are revising for the exam",
  "home.examMode.reviewing": "You said you are revising for the exam.",
  "home.examMode.settings": "Change in Settings",
  "home.examMode.step.exam": "1. Write an old exam on time",
  "home.examMode.step.examHint":
    "Same time and aids as the real one. Then mark it with the marking scheme.",
  "home.examMode.step.examCta": "Write {exam}",
  "home.examMode.step.weak": "2. Practise your weakest topics",
  "home.examMode.step.weakEmpty":
    "Once you have practised a little, your weakest topics show up here.",
  "home.examMode.step.weakAll": "All weakest topics in Progress",
  "home.examMode.step.drill": "3. Timed practice",
  "home.examMode.step.formulas": "4. The formula sheet",
  "home.examMode.step.formulasHint": "The same formulas you bring to the exam.",
  "home.examMode.step.formulasCta": "Open the formula sheet",
  "home.examMode.note":
    "In exam mode you practise everything that is open, not only what we have covered.",
  "onboarding.title": "Get started – 30 seconds",
  "onboarding.intro":
    "Four quick questions so that today's session suits you. You can change everything later in Settings.",
  "onboarding.position.legend": "Where are you in the course?",
  "onboarding.position.following": "I follow the lectures",
  "onboarding.position.missed": "I have missed lectures",
  "onboarding.position.reviewing": "I am revising for the exam",
  "onboarding.position.next": "Next lecture: {lecture}",
  "onboarding.position.last": "Latest lecture: {lecture}",
  "onboarding.missed.legend": "Which did you miss? They come first in today's session.",
  "onboarding.missed.none": "No lectures have been held yet.",
  "onboarding.goal.legend": "Which grade are you aiming for?",
  "onboarding.goal.G": "G (pass)",
  "onboarding.goal.VG": "VG (pass with distinction)",
  "onboarding.goal.unknown": "Not sure yet",
  "onboarding.minutes.legend": "How much time do you have per day?",
  "onboarding.minutes.10": "10 min",
  "onboarding.minutes.20": "20 min",
  "onboarding.minutes.30": "30 min or more",
  "onboarding.math.legend": "How confident are you with the maths (algebra, powers, units)?",
  "onboarding.math.sure": "Confident",
  "onboarding.math.rusty": "A bit rusty",
  "onboarding.math.unsure": "Unsure",
  "onboarding.glossary.title": "Words you will see",
  "onboarding.glossary.moment":
    "Module: one of the course's big topic blocks, for example relativity or atomic physics. Each module belongs to a few lectures.",
  "onboarding.glossary.lectures":
    "F01–F12 and R01–R03: the course's lectures and problem sessions. The card at the top of Home shows the next one.",
  "onboarding.glossary.today":
    "Today's session: a short mix of cards, retests and something new, built from where the course is.",
  "onboarding.glossary.types":
    "A quick check is a short question (about 2 minutes). An exam-style problem is a longer one that you assess yourself.",
  "onboarding.glossary.confidence":
    "Confidence 1–4: how sure you are of your answer. It sets when the problem comes back and never costs points.",
  "onboarding.glossary.principle":
    "The principle step: you first choose which principle to use. It never costs points.",
  "onboarding.glossary.language":
    "The site is in Swedish. The Y&F references are to the English book.",
  "onboarding.glossary.more": "English glossary under Help",
  "onboarding.privacy":
    "Your answers are stored on your account. Teachers see only totals for the whole group, never your answers.",
  "onboarding.save": "Save",
  "onboarding.skip": "Skip",
  "onboarding.saved": "Saved. Today's session follows your choices.",
  "onboarding.skipped": "Skipped. You can answer later in Settings.",

  "settings.onboarding.title": "Get started",
  "settings.onboarding.body":
    "Your answers set today's session and the difficulty practice starts at.",
  "settings.onboarding.edit": "Change my choices",
  "settings.onboarding.help": "How the site works",

  "notFound.title": "Page not found",
  "notFound.body": "The link may be old, or the address may have changed.",
  "notFound.home": "Go to the start page",

  "report.link": "Report an issue",
  "report.title": "Report an issue with this item",
  "report.note": "What is wrong?",
  "report.placeholder": "Briefly describe what looks wrong (answer, unit, wording, figure …).",
  "report.send": "Send",
  "report.sent": "Thank you! Your report has been sent.",

  "source.exam": "Exam {exam} · problem {number}",
  "source.homework": "Hand-in {number} ({term})",
  "source.practiceExam": "Practice exam {paper} · problem {number}",
  "source.testbank": "Test bank",
  "source.translationPending": "Swedish only",

  "numeric.interpreted": "read as",
  "numeric.unparseable": "cannot be read as a number",
  "numeric.unit": "Unit",
  "numeric.dimensionless": "no unit",
  "numeric.key.minus": "Minus",
  "numeric.key.times": "Times ten to the power",
  "numeric.key.e": "Exponent e",
  "numeric.key.comma": "Decimal comma",
  "numeric.textKeyboard": "Text keyboard",
  "numeric.unitMismatch": "not the unit this field expects",

  "error.field.email.invalid": "Enter a valid email address.",
  "error.field.password.min": "The password must be at least 8 characters.",
  "error.field.displayName.invalid": "Enter a name (1–80 characters).",
  "error.field.registrationCode.wrong": "Enter the correct registration code.",
  "error.field.teacherCode.wrong": "Enter the correct teacher code.",
  "error.field.generic": "Check this field.",

  "error.VALIDATION": "Something in the form is not valid.",
  "error.UNAUTHENTICATED": "Please sign in.",
  "error.FORBIDDEN": "You do not have access to this.",
  "error.CSRF": "That request did not come from the app. Reload the page.",
  "error.NOT_FOUND": "Not found.",
  "error.EXAM_ACTIVE": "You already have an exam in progress.",
  "error.EXAM_EXPIRED": "Time is up.",
  "error.EXAM_SUBMITTED": "This exam has already ended.",
  "error.EMAIL_TAKEN": "That email already has an account.",
  "error.EXAM_BANK_INSUFFICIENT": "There are not enough problems for an exam yet.",
  "error.PAYLOAD_TOO_LARGE": "That request carried too much data.",
  "error.RATE_LIMITED": "Too many attempts. Please wait a moment.",
  "error.INTERNAL": "Something went wrong on our side. Please try again.",
  "error.offline": "No connection to the server.",
};
